import React, { useState, useEffect } from 'react'
import { KeyboardAvoidingView, Platform, Image } from 'react-native'
import Svg, { Path } from 'react-native-svg'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { RootStackParamList } from '../types/navigation'
import { useToast } from '../components/Toast'
import { Button, ButtonText, Input, Text, LanguageSwitcher, YStack, XStack, ScrollView } from '@fliisi/pollen'

type LoginMode = 'main' | 'email'

const GoogleLogo = ({ size = 20 }: { size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <Path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <Path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <Path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </Svg>
)

export default function LoginScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [isSignUp, setIsSignUp] = useState(false)
  const [loginMode, setLoginMode] = useState<LoginMode>('main')
  const { session, signInWithEmail, signUpWithEmail } = useAuth()
  const { t } = useLanguage()
  const toast = useToast()

  useEffect(() => {
    if (session) navigation.reset({ index: 0, routes: [{ name: 'BuyerHome' }] })
  }, [session])

  const handleEmailAuth = async () => {
    if (!email || !password) { toast.error(t('error'), t('enter_email_password')); return }
    setLoading(true)
    try {
      await AsyncStorage.setItem('loginMode', 'seller')
      if (isSignUp) { await signUpWithEmail(email, password); toast.success(t('success'), t('check_email')) }
      else { await signInWithEmail(email, password); navigation.reset({ index: 0, routes: [{ name: 'BuyerHome' }] }) }
    } catch (error: any) { toast.error(t('error'), error.message) }
    finally { setLoading(false) }
  }

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    try {
      await AsyncStorage.setItem('loginMode', 'seller')
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: Platform.OS === 'web' ? window.location.origin : 'flikku://auth/callback' },
      })
      if (error) throw error
    } catch (error: any) { toast.error(t('error'), error.message); setGoogleLoading(false) }
  }

  const renderMainOptions = () => (
    <YStack gap="$md" maxWidth={400} w="100%" alignSelf="center">
      <Button variant="secondaryoutline" onPress={handleGoogleSignIn} loading={googleLoading} disabled={loading || googleLoading}>
        <XStack ai="center" gap={12}>
          <GoogleLogo size={20} />
          <ButtonText variant="secondaryoutline">{t('continue_with_google')}</ButtonText>
        </XStack>
      </Button>
      <Button variant="primary" onPress={() => setLoginMode('email')} disabled={loading || googleLoading}>
        <ButtonText>{t('sign_in_with_email')}</ButtonText>
      </Button>
    </YStack>
  )

  const renderEmailForm = () => (
    <YStack gap="$md" maxWidth={400} w="100%" alignSelf="center">
      <Input placeholder={t('email')} value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address" />
      <Input placeholder={t('password')} value={password} onChangeText={setPassword} secureTextEntry />
      <Button variant="primary" onPress={handleEmailAuth} disabled={loading} loading={loading} mt="$sm">
        <ButtonText>{isSignUp ? t('sign_up') : t('sign_in')}</ButtonText>
      </Button>
      <Button variant="ghost" onPress={() => setIsSignUp(!isSignUp)}>
        <ButtonText variant="ghost" style={{ textDecorationLine: 'underline' }}>
          {isSignUp ? t('already_have_account') : t('no_account')}
        </ButtonText>
      </Button>
    </YStack>
  )

  return (
    <YStack f={1} bg="$background">
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView contentContainerStyle={{ flexGrow: 1, paddingBottom: 80 }} keyboardShouldPersistTaps="handled">
          <YStack f={1} jc="center" px="$xl" py="$3xl">
            <XStack position="absolute" top={16} right={24} zIndex={10}>
              <LanguageSwitcher />
            </XStack>
            <Image source={require('../../assets/fliisi-logo.png')} style={{ width: 180, height: 65, alignSelf: 'center', marginBottom: 8 }} resizeMode="contain" />
            <Text preset="h3" tone="secondary" textAlign="center" mb="$3xl">
              {loginMode === 'email' ? (isSignUp ? t('sign_up') : t('sign_in')) : t('sign_in')}
            </Text>
            {loginMode === 'main' && renderMainOptions()}
            {loginMode === 'email' && renderEmailForm()}
          </YStack>
        </ScrollView>
      </KeyboardAvoidingView>
    </YStack>
  )
}
